import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, switchMap, exhaustMap, concatMap, map, catchError, of, from, toArray, forkJoin, throwError } from 'rxjs';
import { RegistryService } from '../../services/registry/registry.service';
import { EntryService } from '../../services/entry/entry.service';
import { RegistryActions } from './registry.actions';
import { ToastService } from '../../services/toast/toast.service';
import { ToastType } from '../../models';
import {
    EntryJobActionType,
    ENTRY_JOB_REJECTION_MESSAGES,
    IBulkCreateEntryJobDraft,
    IBulkCreateEntryJobErrorBody,
    IBulkCreateEntryJobItem,
    IEntryJob,
    IEntryJobErrorBody,
    IUploadFileInfo,
} from '../../models/entry.interface';
import { RegistryIdentityType } from '../../models/registry.interface';
import { entryJobValidationMessages } from '../../utils/entry-job-errors.utils';

@Injectable()
export class RegistryEffects {
    private actions$ = inject(Actions);
    private registryService = inject(RegistryService);
    private entryService = inject(EntryService);
    private toastService = inject(ToastService);

    loadRegistries$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.loadRegistries),
        switchMap(() => {
            return this.registryService.list().pipe(
                map(response => RegistryActions.loadRegistriesSuccess({ registries: response.items })),
                catchError(error => of(RegistryActions.loadRegistriesFailure({ error }))),
            );
        }),
    ));

    createRegistry$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.createRegistry),
        exhaustMap(action => {
            return this.registryService.create(action.request).pipe(
                map(registry => {
                    this.toastService.showToast('Registry created successfully', ToastType.SUCCESS);
                    return RegistryActions.createRegistrySuccess({ registry });
                }),
                catchError(error => {
                    this.toastService.showToast('Failed to create registry', ToastType.ERROR);
                    return of(RegistryActions.createRegistryFailure({ error }));
                }),
            );
        }),
    ));

    updateRegistry$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.updateRegistry),
        exhaustMap(action => {
            return this.registryService.update(action.registryId, action.request).pipe(
                map(registry => {
                    this.toastService.showToast('Registry updated successfully', ToastType.SUCCESS);
                    return RegistryActions.updateRegistrySuccess({ registry });
                }),
                catchError(error => {
                    this.toastService.showToast('Failed to update registry', ToastType.ERROR);
                    return of(RegistryActions.updateRegistryFailure({ error }));
                }),
            );
        }),
    ));

    deleteRegistry$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.deleteRegistry),
        exhaustMap(action => {
            return this.registryService.delete(action.registryId, action.forceDelete).pipe(
                map(response => {
                    this.toastService.showToast('Registry deleted successfully', ToastType.SUCCESS);
                    return RegistryActions.deleteRegistrySuccess({ response });
                }),
                catchError(error => {
                    this.toastService.showToast('Failed to delete registry', ToastType.ERROR);
                    return of(RegistryActions.deleteRegistryFailure({ error }));
                }),
            );
        }),
    ));

    loadSelectedRegistry$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.loadSelectedRegistry),
        switchMap(action => {
            return this.registryService.get(action.registryId).pipe(
                map(registry => RegistryActions.loadSelectedRegistrySuccess({ registry })),
                catchError(error => {
                    this.toastService.showToast('Failed to load registry', ToastType.ERROR);
                    return of(RegistryActions.loadSelectedRegistryFailure({ error }));
                }),
            );
        }),
    ));

    loadEntries$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.loadEntries),
        switchMap(action => {
            return this.entryService.listEntries({ registry_id: action.registryId }).pipe(
                map(entries => RegistryActions.loadEntriesSuccess({ registryId: action.registryId, entries })),
                catchError(error => {
                    this.toastService.showToast('Failed to load entries', ToastType.ERROR);
                    return of(RegistryActions.loadEntriesFailure({ registryId: action.registryId, error }));
                }),
            );
        }),
    ));

    loadEntryJobs$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.loadEntryJobs),
        switchMap(action => {
            return this.entryService.listEntryJobs({
                registry_id: action.registryId,
                status: action.status,
            }).pipe(
                map(entryJobs => RegistryActions.loadEntryJobsSuccess({
                    registryId: action.registryId,
                    status: action.status,
                    entryJobs,
                })),
                catchError(error => {
                    this.toastService.showToast('Failed to load entry jobs', ToastType.ERROR);
                    return of(RegistryActions.loadEntryJobsFailure({
                        registryId: action.registryId,
                        status: action.status,
                        error,
                    }));
                }),
            );
        }),
    ));

    reloadAfterBulkCreateEntriesSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.bulkCreateEntriesSuccess),
        switchMap(action => [
            RegistryActions.loadEntries({ registryId: action.result.registry_id }),
            RegistryActions.loadEntryJobs({ registryId: action.result.registry_id }),
        ]),
    ));

    reloadAfterBulkCreateEntriesFailure$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.bulkCreateEntriesFailure),
        switchMap(action => [
            RegistryActions.loadEntries({ registryId: action.registryId }),
            RegistryActions.loadEntryJobs({ registryId: action.registryId }),
        ]),
    ));

    createEntry$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.createEntry),
        exhaustMap(action => {
            const { registryId, identityType, parameters, files } = action;
            const job$ = files.length === 0
                ? this.createEntryJobDirect(registryId, identityType, parameters)
                : this.uploadThenCreateEntryJob(registryId, identityType, parameters, files);

            return job$.pipe(
                map(entryJob => {
                    this.toastService.showToast('Entry job created successfully', ToastType.SUCCESS);
                    return RegistryActions.createEntrySuccess({ entryJob });
                }),
                catchError(error => {
                    this.toastService.showToast(this.entryJobErrorMessage(error, 'Failed to create entry job'), ToastType.ERROR);
                    return of(RegistryActions.createEntryFailure({ error }));
                }),
            );
        }),
    ));

    bulkCreateEntries$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.bulkCreateEntries),
        exhaustMap(action => {
            const { registryId, identityType, entryJobs } = action;
            const entryJobUploads$ = entryJobs.map(entryJob => this.prepareBulkEntryJobWithUploads(registryId, entryJob));
            return forkJoin(entryJobUploads$).pipe(
                switchMap(preparedEntryJobs => this.entryService.bulkCreateEntryJobs({
                    registry_id: registryId,
                    identity_type: identityType,
                    entry_jobs: preparedEntryJobs,
                }).pipe(
                    map(result => {
                        this.toastService.showToast('Entry jobs created successfully', ToastType.SUCCESS);
                        return RegistryActions.bulkCreateEntriesSuccess({ result });
                    }),
                    catchError(error => {
                        this.toastService.showToast(this.bulkEntryJobErrorMessage(error), ToastType.ERROR);
                        return of(RegistryActions.bulkCreateEntriesFailure({ registryId, error }));
                    }),
                )),
                catchError(error => {
                    this.toastService.showToast(this.bulkEntryJobErrorMessage(error, 'Failed to upload entry images'), ToastType.ERROR);
                    return of(RegistryActions.bulkCreateEntriesFailure({ registryId, error }));
                }),
            );
        }),
    ));

    updateEntry$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.updateEntry),
        exhaustMap(action => {
            const { registryId, identityType, parameters, files } = action;
            const job$ = files.length === 0
                ? this.createEntryJobDirect(registryId, identityType, parameters, 'UPDATE')
                : this.uploadThenCreateEntryJob(registryId, identityType, parameters, files, 'UPDATE');

            return job$.pipe(
                map(entryJob => {
                    this.toastService.showToast('Entry update job created successfully', ToastType.SUCCESS);
                    return RegistryActions.updateEntrySuccess({ registryId, entryJob });
                }),
                catchError(error => {
                    this.toastService.showToast(this.entryJobErrorMessage(error, 'Failed to create entry update job'), ToastType.ERROR);
                    return of(RegistryActions.updateEntryFailure({ error }));
                }),
            );
        }),
    ));

    loadEntriesAfterUpdate$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.updateEntrySuccess),
        map(action => RegistryActions.loadEntries({ registryId: action.registryId })),
    ));

    deleteEntries$ = createEffect(() => this.actions$.pipe(
        ofType(RegistryActions.deleteEntries),
        exhaustMap(action =>
            from(action.entryIds).pipe(
                concatMap(entryId =>
                    this.entryService.createEntryJob({
                        registry_id: action.registryId,
                        identity_type: action.identityType,
                        action_type: 'DELETE',
                        parameters: { entry_id: entryId },
                    })
                ),
                toArray(),
                map(entryJobs => {
                    this.toastService.showToast('Delete job created successfully', ToastType.SUCCESS);
                    return RegistryActions.deleteEntriesSuccess({ entryJobs });
                }),
                catchError(error => {
                    this.toastService.showToast('Failed to create delete job', ToastType.ERROR);
                    return of(RegistryActions.deleteEntriesFailure({ error }));
                }),
            )
        ),
    ));

    private entryJobErrorMessage(error: unknown, defaultMessage: string): string {
        if (!(error instanceof HttpErrorResponse)) {
            return defaultMessage;
        }
        const body = error.error as IEntryJobErrorBody | string | undefined;
        if (error.status === 409) {
            if (typeof body === 'string') {
                return body;
            }
            if (body?.message) {
                return body.message;
            }
        }
        if (error.status === 400) {
            const [message] = entryJobValidationMessages(error.error).request;
            if (message) {
                return message;
            }
        }
        return defaultMessage;
    }

    private bulkEntryJobErrorMessage(error: unknown, defaultMessage = 'Failed to create entry jobs'): string {
        if (!(error instanceof HttpErrorResponse)) {
            return defaultMessage;
        }
        const body = error.error as IBulkCreateEntryJobErrorBody | string | undefined;
        if (typeof body === 'string') {
            return body;
        }
        const rejected = (body?.items ?? []).filter(item => item.status === 'REJECTED');
        if (error.status === 422 && rejected.length > 0) {
            const reason = rejected[0].rejections[0]?.reason;
            const message = reason ? ENTRY_JOB_REJECTION_MESSAGES[reason] : 'One or more entries are invalid';
            return `${message}. Please review the highlighted entries.`;
        }
        if (error.status === 400) {
            return this.bulkValidationErrorMessage(error.error, defaultMessage);
        }
        if (body?.message) {
            return body.message;
        }
        return defaultMessage;
    }

    /**
     * A request the API refused to validate reports which entry each problem
     * belongs to, so a problem with the request is worded on its own and a
     * problem with an entry sends the user to the entry that carries it.
     */
    private bulkValidationErrorMessage(body: unknown, defaultMessage: string): string {
        const { request, entries } = entryJobValidationMessages(body);
        if (request.length > 0) {
            return request[0];
        }
        if (entries.size > 0) {
            const [messages] = [...entries.values()];
            const message = messages[0] ?? 'One or more entries are invalid';
            return `${message}. Please review the highlighted entries.`;
        }
        return defaultMessage;
    }

    private prepareBulkEntryJobWithUploads(
        registryId: string,
        draft: IBulkCreateEntryJobDraft,
    ): Observable<IBulkCreateEntryJobItem> {
        const { action_type, parameters, files } = draft;
        if (files.length === 0) {
            return of({ action_type, parameters });
        }
        return this.uploadFiles(registryId, files).pipe(
            map(stagingPaths => ({ action_type, parameters: { ...parameters, staging_paths: stagingPaths } })),
        );
    }

    private createEntryJobDirect(
        registryId: string,
        identityType: RegistryIdentityType,
        parameters: Record<string, unknown>,
        actionType: EntryJobActionType = 'CREATE',
    ): Observable<IEntryJob> {
        return this.entryService.createEntryJob({
            registry_id: registryId,
            identity_type: identityType,
            action_type: actionType,
            parameters,
        });
    }

    private uploadThenCreateEntryJob(
        registryId: string,
        identityType: RegistryIdentityType,
        parameters: Record<string, unknown>,
        files: File[],
        actionType: EntryJobActionType = 'CREATE',
    ): Observable<IEntryJob> {
        return this.uploadFiles(registryId, files).pipe(
            concatMap(stagingPaths =>
                this.createEntryJobDirect(registryId, identityType, {
                    ...parameters,
                    staging_paths: stagingPaths,
                }, actionType)
            ),
        );
    }

    /**
     * Each file is uploaded under a generated name: staging paths are keyed by
     * filename, so two files that share a name would otherwise collide.
     */
    private uploadFilename(file: File): string {
        const extensionStart = file.name.lastIndexOf('.');
        const extension = extensionStart > 0 ? file.name.slice(extensionStart) : '';
        return `${crypto.randomUUID()}${extension}`;
    }

    private uploadFiles(registryId: string, files: File[]): Observable<string[]> {
        return this.entryService.getUploadSignedUrls({
            registry_id: registryId,
            filenames: files.map(file => this.uploadFilename(file)),
        }).pipe(
            concatMap(signedUrlResponse => {
                const targets = signedUrlResponse.files;
                if (targets.length !== files.length) {
                    return throwError(() => new Error(`Expected ${files.length} upload target(s), received ${targets.length}`));
                }
                const uploads = files.map((file, index) => this.uploadFile(targets[index], file));
                return from(Promise.all(uploads)).pipe(
                    map(() => targets.map(target => target.staging_path)),
                );
            }),
        );
    }

    private uploadFile(fileInfo: IUploadFileInfo, file: File): Promise<void> {
        return fetch(fileInfo.signed_url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
        }).then(response => {
            if (!response.ok) {
                throw new Error(`Upload failed for ${fileInfo.filename}: ${response.status}`);
            }
        });
    }
}
